/**
 * DobPicker — three separate Day / Month / Year dropdowns.
 *
 * Why not a native <input type="date">?
 *   • On mobile Safari / Chrome you have to scroll-wheel through every month
 *     to get to 1970. With three dropdowns you can pick the year in one tap.
 *   • Older users (a large slice of the oodel patron base) consistently
 *     reported confusion with the calendar UI.
 *
 * State model — CRITICAL:
 *   The parent stores the value as a single YYYY-MM-DD string, which is only
 *   meaningful when ALL three parts are picked. We CANNOT round-trip partial
 *   state through the parent because composing "Y--" or "-M-" is invalid →
 *   the picker would emit "" → the parent re-renders "" → the user's first
 *   selection vanishes. So we keep `{y, m, d}` in LOCAL state, sync FROM the
 *   parent when it changes from outside (controlled-component pattern), and
 *   only emit `onChange(YYYY-MM-DD)` once every part is filled (or "" to
 *   clear). Days auto-clamp when month/year changes (Feb 30 → Feb 28/29).
 */
import { useEffect, useState } from "react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MIN_AGE = 13;   // oodel doesn't onboard minors below 13.
const MAX_AGE = 100;  // sanity upper bound.

function daysInMonth(month1Based, year) {
  if (!month1Based || !year) return 31;
  // JS Date trick: day 0 of next month = last day of this month.
  return new Date(year, month1Based, 0).getDate();
}

function parse(value) {
  if (!value) return { y: "", m: "", d: "" };
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return { y: "", m: "", d: "" };
  return { y: m[1], m: String(parseInt(m[2], 10)), d: String(parseInt(m[3], 10)) };
}

function compose(y, m, d) {
  if (!y || !m || !d) return "";
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

export default function DobPicker({ value, onChange, required = false, testIdPrefix = "dob", className = "" }) {
  // LOCAL state holds the partial selection. Initialize from the controlled
  // `value` prop; resync only when the prop changes to a different composed
  // string (i.e. external edit) — NOT on every parent re-render, which would
  // wipe a partial selection.
  const [parts, setParts] = useState(() => parse(value || ""));
  useEffect(() => {
    const fromProp = parse(value || "");
    const composedLocal = compose(parts.y, parts.m, parts.d);
    // Only resync if the parent's composed value drifted from ours. Avoids
    // clobbering an in-flight partial selection.
    if ((value || "") !== composedLocal) {
      setParts(fromProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const years = (() => {
    const arr = [];
    for (let yr = currentYear - MIN_AGE; yr >= currentYear - MAX_AGE; yr--) arr.push(yr);
    return arr;
  })();

  const maxDay = daysInMonth(parseInt(parts.m, 10), parseInt(parts.y, 10));

  const setPart = (part, raw) => {
    const next = { ...parts, [part]: raw };
    const newMax = daysInMonth(parseInt(next.m, 10), parseInt(next.y, 10));
    if (next.d && parseInt(next.d, 10) > newMax) next.d = String(newMax);
    setParts(next);
    onChange(compose(next.y, next.m, next.d));
  };

  // Tailwind: same look-and-feel as the other Sel selects used in the profile form.
  const selCls = "border-2 border-neutral-300 rounded-none px-3 py-2 bg-white text-sm focus:outline-none focus:border-black";

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid={testIdPrefix}>
      <select
        className={`${selCls} w-24`}
        value={parts.d}
        required={required}
        onChange={(e) => setPart("d", e.target.value)}
        data-testid={`${testIdPrefix}-day`}
        aria-label="Day"
      >
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
      <select
        className={`${selCls} w-28`}
        value={parts.m}
        required={required}
        onChange={(e) => setPart("m", e.target.value)}
        data-testid={`${testIdPrefix}-month`}
        aria-label="Month"
      >
        <option value="">Month</option>
        {MONTHS.map((name, idx) => (
          <option key={name} value={idx + 1}>{name}</option>
        ))}
      </select>
      <select
        className={`${selCls} w-28`}
        value={parts.y}
        required={required}
        onChange={(e) => setPart("y", e.target.value)}
        data-testid={`${testIdPrefix}-year`}
        aria-label="Year"
      >
        <option value="">Year</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>
    </div>
  );
}
